import { createContext, useContext } from "react";
import { Api } from "../shared/api";
import { Live } from "../shared/live";
import { OfflineQueue } from "../shared/offline";
import { PhotoQueue } from "../shared/photos";

export const api = new Api("stroyka");
export const live = new Live(api);
export const queue = new OfflineQueue(api, "stroyka");
export const photoQueue = new PhotoQueue(api);
photoQueue.before = () => queue.flush(); // сначала отчёты, потом фото — чтобы фото нашли свой отчёт

export type Me = {
  id: string; login: string; full_name: string; is_admin: boolean;
  role: string | null; // название роли в системе
  must_change_password?: boolean; // первый вход: придумать свой пароль (встреча 24.10.2026)
  permissions: string[]; // права роли (замечание 24.10.2026: роли с правами на всю систему)
  memberships: { project_id: string; building_ids: string[] }[];
};

export const Ctx = createContext<{ me: Me; buildingId: string | null; setBuilding: (id: string) => void; buildings: string[] }>(null as never);
export const useCtx = () => useContext(Ctx);

/** Есть ли у пользователя право (stroyka/permissions.py на сервере). */
export function can(me: Me, perm: string): boolean {
  return me.is_admin || me.permissions.includes(perm);
}

export const ADMIN_PERMS = ["admin.projects", "admin.users", "admin.catalogs"];
export const canAdmin = (me: Me) => ADMIN_PERMS.some((p) => can(me, p));

/** Этапы корпуса до этажей идут в плане как «этажи» с постоянными номерами (сервер: common.STAGE_FLOORS). */
export const PREP_FLOOR = -100, FOUNDATION_FLOOR = -99;
const STAGE_NAME: Record<number, string> = { [PREP_FLOOR]: "Подготовительный этап", [FOUNDATION_FLOOR]: "Фундаментная плита" };
const STAGE_SHORT: Record<number, string> = { [PREP_FLOOR]: "Подг.", [FOUNDATION_FLOOR]: "Фунд." };
export const isStage = (n: number | null | undefined) => n != null && n in STAGE_NAME;
/** «Этаж 5» / «этаж 5», «Подготовительный этап» / «подготовительный этап». */
export function floorName(n: number | null | undefined, capital = true): string {
  if (n == null) return "";
  const s = STAGE_NAME[n] ?? `Этаж ${n}`;
  return capital ? s : s[0].toLowerCase() + s.slice(1);
}
/** Короткая подпись для шахматки и осей графиков: «5», «−1», «Подг.», «Фунд.». */
export const floorShort = (n: number) => STAGE_SHORT[n] ?? String(n);
/** «эт. 5» в строках списков; этап — коротко. */
export const floorAbbr = (n: number | null | undefined) => (n == null ? "" : STAGE_SHORT[n] ?? `эт. ${n}`);

export const STATUS_RU: Record<string, string> = {
  done: "Выполнено", in_progress: "В работе", behind: "Отставание", critical: "Критично", not_started: "Не начато",
  on_time: "В срок", ahead: "Опережение", on_plan: "По плану",
};

export const CHESS_LEGEND = [
  { cls: "sw-done", label: "Выполнено" },
  { cls: "sw-planned", label: "В работе" },
  { cls: "sw-loading", label: "Отставание" },
  { cls: "sw-none", label: "Не начато" },
];

export const GANTT_LEGEND = [
  { cls: "sw-plan", label: "План" },
  { cls: "sw-done", label: "Выполнено" },
  { cls: "sw-planned", label: "В работе" },
  { cls: "sw-loading", label: "Отставание" },
  { cls: "sw-conflict", label: "Критично: держит следующую работу" },
];

// Последний открытый объект запоминается для каждого пользователя отдельно: на общем телефоне или
// компьютере сменщик не должен попасть в чужой объект, к которому у него нет доступа.
const BUILDING_KEY = "rbs.stroyka.building";

export function readBuilding(userId: string): string | null {
  try { return localStorage.getItem(`${BUILDING_KEY}.${userId}`); } catch { return null; }
}
export function saveBuilding(userId: string, id: string | null): void {
  try {
    localStorage.removeItem(BUILDING_KEY); // ключ прежних версий, общий для всех пользователей
    if (id) localStorage.setItem(`${BUILDING_KEY}.${userId}`, id);
    else localStorage.removeItem(`${BUILDING_KEY}.${userId}`);
  } catch { /* хранилище недоступно — просто не запоминаем */ }
}
